import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../../../../interceptors/api";
import { useLayout } from "../../../LayoutContext";
import { DeviceStoryDto } from "../../dto/DeviceStoryDto";
import { placeHolderStories, UserStoryDto } from "../../dto/UserStoryDto";
import CassetteTapeLoadingOverlay from "./CasetteTapeLoadingOverlay";
import CassetteTapeDeckPlayer from "./CassetteTapeDeckPlayer";
import CassetteTapeInlay from "./CassetteTapeInlay";
import CassetteTapeStack from "./CassetteTapeStack";
import "./styles/CassetteTapeShell.css";

const BOOT_TIME = 6000;
const INACTIVITY_MS = 5 * 60 * 1000; // 5 minutes

function CassetteTapeShell() {
  const navigate = useNavigate();
  const { setExhibitionMode } = useLayout();

  // splash / boot
  const [loading, setLoading] = useState(true);
  const [booted, setBooted] = useState(false);

  // data
  const [stories, setStories] = useState<UserStoryDto[]>([]);
  const [selectedStoryId, setSelectedStoryId] = useState<number | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  // inactivity & initial load
  const inactivityTimer = useRef<number>();
  const initialStoriesLoaded = useRef(false);

  const backgroundStyle = location.state
    ?.backgroundStyle as React.CSSProperties;

  const shareStory: UserStoryDto = {
    id: 99999,
    titleEN: "Share your cassette tape story!",
    titleDE: "Teile Deine Kassetten-Geschichte!",
    visualUrl: "/assets/themes/tape/tape_share-story_cover.png",
    textDE:
      "Teile Deine persönliche Kassetten-Geschichte und hilf der Erzählung zu wachsen!",
    textEN: "Share your personal tape story and help the tale grow!",
  };

  const resetTimer = useCallback(() => {
    window.clearTimeout(inactivityTimer.current);
    inactivityTimer.current = window.setTimeout(() => {
      navigate("/exhibits/werkschau", { replace: true });
    }, INACTIVITY_MS);
  }, [navigate]);

  useEffect(() => {
    setExhibitionMode(true);
    return () => {
      setExhibitionMode(false);
    };
  }, [setExhibitionMode]);

  // on mount, kick off the splash → boot
  useEffect(() => {
    const t = window.setTimeout(() => {
      setLoading(false);
      setBooted(true);
    }, BOOT_TIME);
    return () => {
      window.clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    if (!booted || loading) return;

    const events = ["mousemove", "mousedown", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    resetTimer(); // start the timer immediately

    return () => {
      window.clearTimeout(inactivityTimer.current);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [booted, loading, resetTimer]);

  useEffect(() => {
    let isMounted = true;

    const fetchStories = () => {
      api
        .get<UserStoryDto[]>("/api/user-story/device/1")
        .then((res) => {
          if (!isMounted) return;
          const userStories: UserStoryDto[] = [shareStory, ...res.data];
          setStories(userStories);

          if (!initialStoriesLoaded.current) {
            setSelectedStoryId(userStories[1]?.id ?? null);
            initialStoriesLoaded.current = true;
          }
        })
        .catch((err) => {
          console.error("Failed to load stories:", err);
        });
    };

    fetchStories();
    const interval = window.setInterval(fetchStories, 60_000);

    return () => {
      isMounted = false;
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchDeviceStory = () => {
      api
        .get<DeviceStoryDto>("/api/device-story/device/1/exhibition/1")
        .then((res) => {
          if (!isMounted) return;
          if (res.data.audioUrl) {
            setAudioUrl(res.data.audioUrl);
            setError(null);
          } else {
            setError("No device story available.");
          }
        })
        .catch((err) => {
          if (!isMounted) return;
          console.error("Error loading device story", err);
          setError("Failed to load device story.");
        });
    };

    // Fetch immediately on mount and every minute
    fetchDeviceStory();
    const interval = window.setInterval(fetchDeviceStory, 60_000);

    return () => {
      isMounted = false;
      window.clearInterval(interval);
    };
  }, []);

  const [lang, setLang] = useState<"EN" | "DE">("EN");

  const handleBackToStart = () => {
    navigate("/exhibits/werkschau", { replace: true });
  };

  if (loading) {
    return <CassetteTapeLoadingOverlay backgroundStyle={backgroundStyle} />;
  }

  return (
    <div className="tape-shell">
      <div className="tape-shelf">
        <div className="tape-shelf-top">
          <div className="tape-shelf-top-label">
            <div className="shelf-label-text">User Stories</div>
          </div>

          <div className="tape-shelf-top-label-lang-switcher">
            <div className="shelf-label-text">language:</div>
            <div
              className={
                lang === "EN" ? "shelf-label-text active" : "shelf-label-text"
              }
              onClick={() => setLang("EN")}
            >
              English
            </div>
            <div className="shelf-label-text">|</div>
            <div
              className={
                lang === "DE" ? "shelf-label-text active" : "shelf-label-text"
              }
              onClick={() => setLang("DE")}
            >
              Deutsch
            </div>
          </div>

          <CassetteTapeStack
            stories={stories}
            lang={lang}
            selectedId={selectedStoryId!}
            onSelect={setSelectedStoryId}
          />
        </div>
        <div className="tape-shelf-bottom">
          <div className="tape-shelf-bottom-left">
            <div className="tape-shelf-bottom-left-label">
              <div className="shelf-label-text">Cassette Tape Story</div>
            </div>
            {audioUrl && !error && (
              <CassetteTapeDeckPlayer
                audioUrl={audioUrl}
                onEject={handleBackToStart}
              />
            )}
          </div>
          <div
            className="tape-shelf-bottom-right"
            style={{
              backgroundImage: `url("${stories.find((userStory) => userStory.id === selectedStoryId)!.visualUrl}")`,
              backgroundPosition: "center",
            }}
          >
            <div className="tape-shelf-bottom-right-label">
              <div className="shelf-label-text">Selected user story</div>
            </div>
            <CassetteTapeInlay
              story={
                stories.find((userStory) => userStory.id === selectedStoryId)!
              }
              lang={lang}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CassetteTapeShell;
